import { Icon, Illustration, Input, Text, Tooltip, HStack } from '@genial/components';
import { BoxRadius, Color } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { DrawerActions, useNavigation } from '@react-navigation/native';
import { debounce } from 'lodash';
import { Divider, Menu } from 'native-base';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  NativeSyntheticEvent,
  Pressable,
  TextInputChangeEventData,
  View,
  ViewStyle,
} from 'react-native';
import { useRecoilValue } from 'recoil';

import { useAbsoluteRoute } from '../../hooks/useAbsoluteRoute';
import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { ImpersonateMenuContext } from '../../pages/ImpersonateMenu';
import { ImpersonateMenuPage } from '../../pages/ImpersonateMenu/types';
import { useInitialRouteName } from '../../routes/hooks';
import { AllSignedInRoutes, SignedInNavigationScreenNavigationProp } from '../../routes/types';
import { customerGlobalSearchQuery } from '../../store/customer/selector';
import { CustomerGlobalSearchResponseNormalized } from '../../store/customer/types';
import {
  masterStateSelectedAdvisorNameSelector,
  masterStateSelectedAdvisorSelector,
} from '../../store/master/selector';
import { sessionState } from '../../store/session/atom';
import { FeatureToggle, useUnleashFlags } from '../../utils/useUnleashFlags';
import { AppMenu } from '../AppMenu';

import { Avatar } from './components/Avatar';
import { useMenuSearchKeywords } from './hooks';
import { menuItemBackgroundColor, menuItemNotFoundBackgroundColor, styles } from './styles';

export const SignedInHeader: React.FC = React.memo(() => {
  const currentRoute = useAbsoluteRoute();
  const { b2bShowModuleCustomers } = useUnleashFlags(FeatureToggle.Customers);
  const { userProfile, isMaster } = useRecoilValue(sessionState);
  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);
  const selectedAdvisorName = useRecoilValue(masterStateSelectedAdvisorNameSelector);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const [searchText, setSearchText] = useState('');
  const [autoCompleteSearchTerm, setAutoCompleteSearchTerm] = useState('');
  const { setIsOpen: setIsImpersonateMenuOpen, isOpen: isImpersonateMenuOpen } =
    useContext(ImpersonateMenuContext);
  const [customerNotFound, setCustomerNotFound] = useState(false);
  const { isOpen: isAppMenuOpen } = useContext(AppMenu.Context);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const defaultInitialRouteName = useInitialRouteName();
  const [autoCompleteCustomers, setAutoCompleteCustomers] = useState<
    CustomerGlobalSearchResponseNormalized[]
  >([]);
  const { data: currentCustomers, loading: searching } = useRecoilQuery(
    customerGlobalSearchQuery({ searchTerm: autoCompleteSearchTerm })
  );
  const autocompleteMenuPlacement = useBreakpointValue({
    base: 'bottom',
    sm: 'bottom left',
  } as const);

  const autocompleteSearchMenuItems = useMenuSearchKeywords(autoCompleteSearchTerm);
  const handleToggleDrawer = useCallback(() => {
    if (isImpersonateMenuOpen) {
      return;
    }
    navigation.dispatch(DrawerActions.toggleDrawer());
  }, [isImpersonateMenuOpen, navigation]);

  const handleToggleImpersonateDrawer = useCallback(
    (page: ImpersonateMenuPage) => {
      if (isAppMenuOpen) {
        return;
      }

      // if (!isImpersonateMenuOpen) {
      //   navigation.navigate({ key: 'ImpersonateMenu' });
      // }

      setIsImpersonateMenuOpen(!isImpersonateMenuOpen, page);
    },
    [isAppMenuOpen, isImpersonateMenuOpen, navigation, setIsImpersonateMenuOpen]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebouncedAutoCompleteSearchTerm = useCallback(
    debounce((newSearchTerm: string) => setAutoCompleteSearchTerm(newSearchTerm), 1000),
    []
  );

  const handleChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setSearchText(e.nativeEvent.text);
      setDebouncedAutoCompleteSearchTerm(e.nativeEvent.text);
    },
    [setDebouncedAutoCompleteSearchTerm]
  );

  const handleClearSearch = useCallback(() => {
    setSearchText('');
    setAutoCompleteSearchTerm('');
  }, []);

  const handleCloseAutoComplete = useCallback(() => {
    setAutoCompleteCustomers([]);
    setIsMenuOpen(false);
  }, []);

  const handleGoToClientSearch = useCallback(() => {
    handleCloseAutoComplete();
    handleClearSearch();
    navigation.navigate('Customers', { search: searchText });
  }, [handleClearSearch, handleCloseAutoComplete, navigation, searchText]);

  const handleGoToClientSearchLegacy = useCallback(() => {
    handleCloseAutoComplete();
    handleClearSearch();
    navigation.navigate('AngularFrame_Customers', { state: 'app.meusClientes.atendimentos' });
  }, [handleClearSearch, handleCloseAutoComplete, navigation]);

  const handleSelectClient = useCallback(
    (selectedClient: CustomerGlobalSearchResponseNormalized) => {
      handleCloseAutoComplete();
      handleClearSearch();

      const method =
        currentRoute.name === 'AngularFrame_CustomerDetail'
          ? navigation.replace
          : navigation.navigate;

      method('AppDrawer', {
        screen: 'AngularFrame_CustomerDetail',
        params: {
          state: 'app.posicaoOnline.posicao',
          DocumentoCliente: Utils.Unmask.CpfOrCnpj(selectedClient.cpfCnpjNormalized),
          IdConta: selectedClient.accountId,
        },
      });
    },
    [handleClearSearch, handleCloseAutoComplete, navigation, currentRoute]
  );

  const handleSelectAutoCompleteRoute = useCallback(
    (routeName: keyof AllSignedInRoutes, params: any) => {
      const method = currentRoute.name === routeName ? navigation.replace : navigation.navigate;

      method('AppDrawer', {
        screen: routeName,
        params,
      });
    },
    [currentRoute, navigation]
  );

  const navigationToInitialRoute = useCallback(() => {
    navigation.navigate(defaultInitialRouteName);
  }, [navigation, defaultInitialRouteName]);

  const hasSearch = searchText.length > 0;
  const userInfo = useBreakpointValue({
    base: null,
    sm: (
      <Pressable
        onPress={() =>
          handleToggleImpersonateDrawer(
            isMaster ? ImpersonateMenuPage.Impersonate : ImpersonateMenuPage.User
          )
        }
        style={styles.userInfoContainer}>
        <Icon name="IdCard" size="xxs" color={Color['brand.tertiary.principal']} />
        <View style={styles.usernameContainer}>
          <Text fontSize="xs" color="neutral.medium">
            Visualizando dados de:
          </Text>
          <Text fontSize="md">
            {selectedAdvisor !== undefined ? selectedAdvisorName : userProfile?.nomeRecurso}
          </Text>
        </View>
        {isMaster && (
          <Icon
            name={isImpersonateMenuOpen ? 'ChevronUp' : 'ChevronDown'}
            size="xxs"
            color={Color['brand.tertiary.principal']}
          />
        )}
      </Pressable>
    ),
  });

  const searchResponsiveStyle = useBreakpointValue<ViewStyle>({
    base: styles.searchContainerMobile,
    sm: {},
  });

  const normalizedAutoCompleteCustomers = useMemo(() => {
    const menuItems = autoCompleteCustomers.map((customer) => (
      <Menu.Item
        key={customer.cpfCnpjNormalized}
        onPress={() => handleSelectClient(customer)}
        _focus={menuItemBackgroundColor}
        _hover={menuItemBackgroundColor}
        style={styles.autoCompleteMenuItem}>
        <Tooltip label={customer.customerNameNormalized}>
          <View style={styles.autoCompleteMenuContainer}>
            <Text ellipsizeMode="tail" numberOfLines={1} variant="bold" flex={1}>
              {customer.customerNameNormalized}
            </Text>
            <HStack pt="stack.quarck" justifyContent="space-between" space="stack.xxxs">
              <Text fontSize="xs" color="neutral.black" w="1/3">
                {customer.cpfCnpjNormalized}
              </Text>
              <Text fontSize="xs" color="neutral.black" w="1/3">
                {customer.custodyAccountNormalized}
              </Text>
              <Text fontSize="xs" color="neutral.medium" w="1/3">
                {customer.sinacorAccountNormalized}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" space="stack.xxxs">
              <Text fontSize="xs" color="neutral.medium" w="1/3">
                CPF
              </Text>
              <Text fontSize="xs" color="neutral.medium" w="1/3">
                Conta de Custódia
              </Text>
              <Text fontSize="xs" color="neutral.light" w="1/3">
                Conta Sinacor
              </Text>
            </HStack>
          </View>
        </Tooltip>
      </Menu.Item>
    ));

    if (autoCompleteCustomers.length > 1) {
      menuItems.push(<Divider style={styles.autoCompleteMenuItemDivider} />);
      menuItems.push(
        <Menu.Item
          key="clear"
          onPress={handleGoToClientSearchLegacy}
          _focus={menuItemBackgroundColor}
          _hover={menuItemBackgroundColor}
          style={[styles.autoCompleteMenuItem, styles.autoCompleteMenuItemAction]}>
          <View style={styles.autoCompleteMenuItemAction}>
            <Icon name="Clients" size="xxs" color={Color['neutral.medium']} />
            <Text>Ir para Meus Clientes</Text>
          </View>
        </Menu.Item>
      );

      if (b2bShowModuleCustomers) {
        menuItems.push(
          <Menu.Item
            key="clear"
            onPress={handleGoToClientSearch}
            _focus={menuItemBackgroundColor}
            _hover={menuItemBackgroundColor}
            style={styles.autoCompleteMenuItem}>
            <Icon name="Clients" size="xxs" color={Color['neutral.medium']} />
            <View style={styles.autoCompleteMenuItemAction}>
              <Text flex={1}>Ir para Meus Clientes</Text>
              <View style={styles.betaBadge}>
                <Text color="neutral.white">Beta</Text>
              </View>
            </View>
          </Menu.Item>
        );
      }
    }

    return menuItems;
  }, [
    autoCompleteCustomers,
    b2bShowModuleCustomers,
    handleGoToClientSearch,
    handleGoToClientSearchLegacy,
    handleSelectClient,
  ]);

  const normalizedAutoCompleteMenus = useMemo(() => {
    return autocompleteSearchMenuItems.map((menu) => (
      <Menu.Item
        key={menu.routeName}
        onPress={() =>
          handleSelectAutoCompleteRoute(menu.routeName as keyof AllSignedInRoutes, menu.routeParams)
        }
        _focus={menuItemBackgroundColor}
        _hover={menuItemBackgroundColor}
        style={styles.autoCompleteMenuItem}>
        <Icon name={menu.menuIcon} size="xxs" color={Color['neutral.medium']} />
        <View style={styles.autoCompleteMenuItemAction}>
          <Text ellipsizeMode="tail" noOfLines={1} flex={1}>
            {menu.routeText}
          </Text>
          {menu.isBeta && (
            <View style={styles.betaBadge}>
              <Text color="neutral.white">Beta</Text>
            </View>
          )}
        </View>
      </Menu.Item>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocompleteSearchMenuItems]);

  useEffect(() => {
    if (currentCustomers?.data.length || autocompleteSearchMenuItems.length) {
      if (currentCustomers?.data.length) {
        setAutoCompleteCustomers(currentCustomers.data);
      }
      setCustomerNotFound(false);
      setIsMenuOpen(true);
    } else if (customerNotFound) {
      setIsMenuOpen(true);
    } else {
      setIsMenuOpen(false);
      setAutoCompleteCustomers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocompleteSearchMenuItems.length, currentCustomers?.data]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        !autoCompleteCustomers?.length &&
        !autocompleteSearchMenuItems.length &&
        searchText.length > 2
      ) {
        setCustomerNotFound(true);
      } else {
        setCustomerNotFound(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [autocompleteSearchMenuItems.length, autoCompleteCustomers?.length, searchText.length]);

  return (
    <View style={styles.container}>
      <View style={styles.searchAndHamburgerContainer}>
        <Pressable onPress={handleToggleDrawer}>
          <Icon name="Menu" size="sm" color={Color['brand.tertiary.principal']} />
        </Pressable>
        <Pressable onPress={navigationToInitialRoute}>
          <Illustration
            name="GenialBlue"
            width={Utils.Icon.Sizes.sm}
            height={Utils.Icon.Sizes.sm}
          />
        </Pressable>
        <View style={searchResponsiveStyle}>
          <Input
            flex={{ base: 1, md: undefined }}
            name="search"
            autoComplete="off"
            placeholder="Buscar no portal"
            value={searchText}
            onChange={handleChange}
            variant="rounded"
            defaultValue={searchText}
            hasShadow={false}
            inputRightElement={
              searching ? (
                <View style={styles.searchIndicator}>
                  <ActivityIndicator color={Color['brand.tertiary.light']} />
                </View>
              ) : (
                <Pressable
                  onPress={hasSearch ? handleClearSearch : null}
                  style={styles.searchPressable}>
                  <Icon testID="qa" name={hasSearch ? 'Close' : 'Search'} size="xxxs" />
                </Pressable>
              )
            }
          />
          <Menu
            borderRadius={BoxRadius.sm}
            style={styles.autoCompleteMenu}
            onClose={handleCloseAutoComplete}
            placement={autocompleteMenuPlacement}
            trigger={(triggerProps) => <View {...triggerProps} />}
            isOpen={isMenuOpen}>
            {(autoCompleteCustomers.length > 0 || searching) &&
              (searching ? (
                <Menu.Item
                  _focus={menuItemBackgroundColor}
                  _hover={menuItemBackgroundColor}
                  style={styles.autoCompleteMenuItem}>
                  <Text>Buscando clientes</Text>
                  <ActivityIndicator size="small" color={Color['brand.tertiary.light']} />
                </Menu.Item>
              ) : (
                normalizedAutoCompleteCustomers
              ))}
            {autocompleteSearchMenuItems.length > 0 && (
              <Menu.Group title="Páginas">{normalizedAutoCompleteMenus}</Menu.Group>
            )}
            {customerNotFound && (
              <Menu.Item
                _focus={menuItemNotFoundBackgroundColor}
                _hover={menuItemNotFoundBackgroundColor}
                style={styles.autoCompleteMenuItem}>
                <View style={[styles.autoCompleteMenuItem]}>
                  <Text fontWeight={'bold'}>Nenhum resultado corresponde a sua pesquisa.</Text>
                  <Text>Verifique seu nível de acesso e tente novamente.</Text>
                </View>
              </Menu.Item>
            )}
          </Menu>
        </View>
      </View>
      <View style={styles.userContainer}>
        {userInfo}
        <Avatar onPress={() => handleToggleImpersonateDrawer(ImpersonateMenuPage.User)} />
      </View>
    </View>
  );
});
